import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { useI18n } from '@/contexts/I18nProvider';
import { getCurrentLanguage, resolveAutoLanguage } from '@/utils/languageDetection';

interface TranslationMap {
  [key: string]: string;
}

export function useTranslation(languageId?: string, tenantId?: string) {
  const [translations, setTranslations] = useState<TranslationMap>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentLanguage: currentLanguageCode } = useI18n();
  const [currentLanguageId, setCurrentLanguageId] = useState<string | null>(null);

  const loadTranslations = async (langId: string) => {
    if (!langId) return;

    try {
      setLoading(true);
      setError(null);

      const user = apiService.getStoredUser();

      let translationList = null;
      if(tenantId) {
        const { tenant } = await apiService.getTenantBySlug(tenantId);
        const languages = await apiService.getTenantLanguages(tenant.id);
        let currentLanguage = currentLanguageCode || getCurrentLanguage(languages);
        if(currentLanguage === 'auto') {
          currentLanguage = resolveAutoLanguage(languages.map((lang: { code: string }) => lang.code));
        } else if(currentLanguageCode.length == 2) {
          currentLanguage = languages.find((lang: { code: string }) => lang.code.split('-')[0] === currentLanguageCode)?.code;
        }
        translationList = await apiService.getLanguageTranslationsByLanguageCode(tenantId, currentLanguage);
        langId = languages.find((lang: { code: string }) => lang.code === currentLanguage)?.id;

      } else {
        if(!user) return;

        translationList = await apiService.getLanguageTranslations(user?.tenantId, langId);
      }

      // Convert array to map for easier lookup
      const translationMap: TranslationMap = {};
      translationList.forEach((translation: { key: string; value: string }) => {
        translationMap[translation.key] = translation.value;
      });

      setTranslations(translationMap);
      setCurrentLanguageId(langId);
    } catch (err) {
      console.error('Failed to load translations:', err);
      setError('Failed to load translations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentLanguageCode && tenantId) {
      loadTranslations(currentLanguageCode);
    }
}, [currentLanguageCode, tenantId]);

  // Load default language translations on mount
  useEffect(() => {
    const loadDefaultTranslations = async () => {
      try {
        const user = apiService.getStoredUser();
        if (!user?.tenantId) return;

        // Get languages and find the default one
        const languages = await apiService.getTenantLanguages(user.tenantId);
        const defaultLang = languages.find((lang: { id: string; code: string; isDefault?: boolean }) => lang.isDefault) || languages.find((lang: { id: string; code: string; isActive?: boolean }) => lang.isActive);

        if (defaultLang) {
          await loadTranslations(defaultLang.id);
        }
      } catch (err) {
        console.error('Failed to load default translations:', err);
      }
    };

    loadDefaultTranslations();
  }, []);

  // Load specific language when languageId changes
  useEffect(() => {
    if (languageId && languageId !== currentLanguageId) {
      loadTranslations(languageId);
    }
  }, [languageId]);

  const t = useCallback((key: string, fallback?: string): string => {
    return translations[key] || fallback || key;
  }, [translations]);

  const reloadTranslations = () => {
    if (languageId) {
      loadTranslations(languageId);
    }
  };

  return {
    t,
    translations,
    loading,
    error,
    reloadTranslations
  };
}
